import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FilePreviewProvider } from "@/context/FilePreviewContext";
import FilePreviewDrawer from "@/components/FilePreviewDrawer";
import { DriverDocsDrawer, STATUS_META } from "@/pages/Compliance";
import SignatureOnFile from "@/components/SignatureOnFile";
import {
  ArrowLeft, ArrowRight, ClipboardCheck, FileSignature, FileText, IdCard, ScrollText, Clock3, FolderOpen,
} from "lucide-react";

const LINK_LINES = [
  { key: "license", label: "Driver's license", route: "/driver/license", Icon: IdCard, testId: "driver-license-link" },
  { key: "application", label: "Job application", route: "/driver/application", Icon: FileText, testId: "driver-application-link" },
  { key: "agreements", label: "Agreements to sign", route: "/driver/agreements", Icon: ScrollText, testId: "driver-agreements-link" },
  { key: "w9", label: "IRS Form W-9", route: "/driver/w9", Icon: FileSignature, testId: "driver-w9-link" },
];

const LINK_STATUS_META = {
  ...STATUS_META,
  pending: { label: "Not signed", Icon: Clock3, cls: "text-slate-600 bg-slate-50 border-slate-200" },
};

// The driver-facing "My documents" home. Contract I moved this out of DriverPortal.jsx into
// its own page so the floating signature card and the scattered license/application/
// agreements/W-9 link lines have one settled home instead of living on top of the rides list.
// Requests-from-the-company, the documents list and the maintenance tab all keep living inside
// DriverDocsDrawer (built in contract H): it is already a three-tab component, so it is
// mounted here as-is rather than split apart, one reused block instead of three duplicated ones.
export default function DriverDocuments() {
  return (
    <FilePreviewProvider>
      <DriverDocumentsInner />
    </FilePreviewProvider>
  );
}

function DriverDocumentsInner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [onboarding, setOnboarding] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsTab, setDocsTab] = useState("docs");

  useEffect(() => {
    if (!user?.driver_id) return;
    api.get("/driver/onboarding").then((r) => setOnboarding(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [user?.driver_id]);

  const onboardingComplete = onboarding && onboarding.first_incomplete >= onboarding.steps.length;
  // A link line shows one status for its whole step, not just its first item: "valid" once the
  // step itself is done (covers "agreements" needing every enabled agreement signed, not just
  // the first one), otherwise the worst status among its items.
  const statusFor = (key) => {
    if (!onboarding) return null;
    const step = onboarding.steps.find((s) => s.key === key);
    if (!step) return null;
    if (step.done) return "valid";
    for (const s of ["expired", "expiring", "pending", "missing"]) {
      if (step.items.some((it) => it.status === s)) return s;
    }
    return "missing";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#005E82] flex items-center justify-center"><ClipboardCheck size={18} /></div>
            <div>
              <div className="font-display font-semibold text-sm">My documents</div>
              <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{user?.name}</div>
            </div>
          </div>
          <button onClick={() => navigate("/driver")} className="min-h-[44px] text-slate-300 text-xs flex items-center gap-1.5 hover:text-white" data-testid="driver-documents-back">
            <ArrowLeft size={14} /> Back to rides
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 pt-5 pb-24 space-y-4" data-testid="driver-documents-page">
        {onboarding && !onboardingComplete && (
          <button
            onClick={() => navigate("/driver/onboarding")}
            className="w-full bg-white border border-[#005E82] rounded-md p-4 text-left hover:bg-slate-50"
            data-testid="onboarding-progress-card"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-display font-semibold text-slate-900 text-sm">Finish your onboarding</div>
                <div className="text-xs text-slate-500 mt-0.5">{onboarding.completed} of {onboarding.total} complete</div>
              </div>
              <ArrowRight size={16} className="text-[#005E82] flex-shrink-0" />
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-3">
              <div className="h-full bg-[#005E82]" style={{ width: `${Math.round((onboarding.completed / onboarding.total) * 100)}%` }} />
            </div>
          </button>
        )}

        <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="driver-link-lines">
          {LINK_LINES.map((line) => {
            const status = statusFor(line.key);
            const meta = status ? (LINK_STATUS_META[status] || LINK_STATUS_META.missing) : null;
            const Icon = line.Icon;
            return (
              <button
                key={line.key}
                onClick={() => navigate(line.route)}
                className="w-full min-h-[52px] px-4 flex items-center justify-between gap-2 hover:bg-slate-50 text-left"
                data-testid={line.testId}
              >
                <span className="flex items-center gap-2 text-sm text-slate-800">
                  <Icon size={14} className="text-[#005E82]" /> {line.label}
                </span>
                {meta && (
                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5 border ${meta.cls}`}>
                    <meta.Icon size={10} /> {meta.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <SignatureOnFile driverId={user?.driver_id} legalName={user?.name} canUpdate />

        <button
          onClick={() => setDocsOpen(true)}
          className="w-full bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-3 hover:bg-slate-50 text-left"
          data-testid="open-docs-drawer"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-slate-900">
            <FolderOpen size={16} className="text-[#005E82]" /> Documents, maintenance and requests
          </span>
          <ArrowRight size={16} className="text-slate-400 flex-shrink-0" />
        </button>
      </div>

      {/* DriverDocsDrawer (from contract H) already bundles the documents checklist, the
          maintenance tab, and (for a driver) a Requests tab into one slide-over. Reused as-is
          here instead of duplicated: opening it from a button keeps that overlay working the
          same way it already does everywhere else it is mounted (Compliance.jsx). */}
      {docsOpen && user?.driver_id && (
        <DriverDocsDrawer
          driver={{ driver_id: user.driver_id, driver_name: user.name }}
          tab={docsTab}
          setTab={setDocsTab}
          onClose={() => setDocsOpen(false)}
        />
      )}
      <FilePreviewDrawer />
    </div>
  );
}
